import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Wheat,
  Factory,
  Truck,
  ShoppingCart,
  Briefcase,
  Building,
  Cpu,
  Heart,
  Hotel,
  CircleDot,
  Calendar,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileText,
  Download,
  Shield,
  DollarSign,
  Percent,
  Timer,
  Lock,
  AlertTriangle,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sector icon mapping
const sectorIcons = {
  agriculture: Wheat,
  manufacturing: Factory,
  logistics: Truck,
  retail: ShoppingCart,
  services: Briefcase,
  construction: Building,
  technology: Cpu,
  healthcare: Heart,
  hospitality: Hotel,
  other: CircleDot
};

const DealDetailPage = () => {
  const { token } = useAuth();
  const { dealId } = useParams();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // EOI Form state
  const [eoiAmount, setEoiAmount] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [riskAcknowledged, setRiskAcknowledged] = useState(false);
  const [ficaConfirmed, setFicaConfirmed] = useState(false);
  const [eoiNotes, setEoiNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);

  // Fetch deal details
  const fetchDeal = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/deals/${dealId}`, { headers });
      setDeal(response.data);
      if (response.data.minimum_investment) {
        setEoiAmount(response.data.minimum_investment.toString());
      }
    } catch (error) {
      console.error('Failed to fetch deal:', error);
      toast.error('Failed to load deal details');
    } finally {
      setLoading(false);
    }
  }, [dealId, token]);

  useEffect(() => {
    fetchDeal();
  }, [fetchDeal]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0).replace('ZAR', 'R');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'open':
        return { label: 'Open', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 };
      case 'closing_soon':
        return { label: 'Closing Soon', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock };
      case 'fully_subscribed':
        return { label: 'Fully Subscribed', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: TrendingUp };
      case 'closed':
        return { label: 'Closed', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: AlertCircle };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-600', icon: CircleDot };
    }
  };

  const getRiskConfig = (rating) => {
    switch (rating) {
      case 'A':
        return { label: 'A - Low Risk', color: 'bg-green-500 text-white', description: 'Strong security, established borrower' };
      case 'B':
        return { label: 'B - Medium Risk', color: 'bg-amber-500 text-white', description: 'Good security, viable business' };
      case 'C':
        return { label: 'C - Higher Risk', color: 'bg-red-500 text-white', description: 'Higher yield, elevated risk' };
      default:
        return { label: rating, color: 'bg-gray-500 text-white', description: '' };
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/deals/${dealId}/documents/${doc.id}/download`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.document_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleSubmitEOI = async () => {
    const amount = parseFloat(eoiAmount);
    
    if (!amount || amount < (deal?.minimum_investment || 0)) {
      toast.error(`Minimum investment is ${formatCurrency(deal?.minimum_investment || 50000)}`);
      return;
    }
    
    if (!termsAccepted || !riskAcknowledged || !ficaConfirmed) {
      toast.error('Please accept all confirmations');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/deals/${dealId}/eoi`, {
        amount,
        terms_accepted: termsAccepted,
        risk_acknowledged: riskAcknowledged,
        fica_confirmed: ficaConfirmed,
        notes: eoiNotes || null
      }, { headers });
      
      setSuccessDialog(true);
      fetchDeal(); // Refresh to show EOI status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit expression of interest');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawEOI = async () => {
    try {
      await axios.delete(`${API_URL}/api/deals/${dealId}/eoi`, { headers });
      toast.success('Expression of Interest withdrawn');
      fetchDeal();
    } catch (error) {
      toast.error('Failed to withdraw EOI');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <p className="text-[#212529] font-medium">Deal not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/deals')}>
          Back to Deal Room
        </Button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(deal.status);
  const StatusIcon = statusConfig.icon;
  const riskConfig = getRiskConfig(deal.risk_rating);
  const SectorIcon = sectorIcons[deal.sector] || CircleDot;
  const canInvest = ['open', 'closing_soon'].includes(deal.status) && !deal.investor_eoi;
  const hasSubmittedEOI = deal.investor_eoi?.status === 'submitted';

  return (
    <div className="space-y-6" data-testid="deal-detail-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        className="text-[#6C757D] hover:text-[#212529]"
        onClick={() => navigate('/deals')}
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Deal Room
      </Button>

      {/* Hero Section */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <div className="bg-gradient-to-br from-[#1D2D2A] to-[#315C52] text-white p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <SectorIcon className="h-7 w-7" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={riskConfig.color}>{deal.risk_rating}</Badge>
                  <Badge className="bg-white/20 text-white border-white/30 border">
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold font-['Manrope']" data-testid="deal-name">
                  {deal.deal_name}
                </h1>
                <p className="text-white/70 mt-1">
                  {deal.sector_info?.name || deal.sector} • {deal.term_months} months
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              <div className="text-center">
                <p className="text-white/70 text-sm">Target</p>
                <p className="text-xl font-bold">{formatCurrency(deal.target_amount)}</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-sm">Committed</p>
                <p className="text-xl font-bold">{formatCurrency(deal.committed_amount)}</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-sm">Rate</p>
                <p className="text-xl font-bold">{deal.interest_rate_min}-{deal.interest_rate_max}%</p>
              </div>
              <div className="text-center">
                <p className="text-white/70 text-sm">Deadline</p>
                <p className="text-xl font-bold">{formatDate(deal.funding_deadline)}</p>
              </div>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-white/70">Funding Progress</span>
              <span className="font-medium">{deal.progress_percentage}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-3">
              <div 
                className="bg-white rounded-full h-3 transition-all" 
                style={{ width: `${Math.min(deal.progress_percentage, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* EOI Status Banner */}
      {deal.investor_eoi && (
        <Card className={`border-0 shadow-sm ${
          deal.investor_eoi.status === 'accepted' ? 'bg-green-50' :
          deal.investor_eoi.status === 'submitted' ? 'bg-blue-50' :
          deal.investor_eoi.status === 'declined' ? 'bg-red-50' : 'bg-gray-50'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {deal.investor_eoi.status === 'accepted' ? (
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                ) : deal.investor_eoi.status === 'submitted' ? (
                  <Clock className="h-6 w-6 text-blue-600" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-red-600" />
                )}
                <div>
                  <p className="font-medium text-[#212529]">
                    Expression of Interest: {deal.investor_eoi.status.charAt(0).toUpperCase() + deal.investor_eoi.status.slice(1)}
                  </p>
                  <p className="text-sm text-[#6C757D]">
                    Amount: {formatCurrency(deal.investor_eoi.amount)} • Submitted: {formatDate(deal.investor_eoi.submitted_at)}
                  </p>
                </div>
              </div>
              {hasSubmittedEOI && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleWithdrawEOI}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Withdraw EOI
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabbed Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="terms" className="data-[state=active]:bg-white">Terms</TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-white">
            Documents ({deal.documents?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="participate" className="data-[state=active]:bg-white">Participate</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">Deal Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: deal.description_html }}
                  />
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">Borrower Profile</CardTitle>
                  <CardDescription>Anonymised borrower information</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-[#212529]">{deal.borrower_profile}</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">Use of Proceeds</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#212529]">{deal.use_of_proceeds}</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">Repayment Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-[#212529]">{deal.repayment_source}</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="space-y-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">Risk Assessment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-4">
                    <Badge className={`${riskConfig.color} text-lg px-4 py-1`}>{deal.risk_rating}</Badge>
                    <span className="font-medium">{riskConfig.label}</span>
                  </div>
                  <p className="text-sm text-[#6C757D]">{deal.risk_info?.description || riskConfig.description}</p>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">Security</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-[#1D2D2A] mt-0.5" />
                    <p className="text-[#212529]">{deal.collateral_description}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Terms Tab */}
        <TabsContent value="terms">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Indicative Terms</CardTitle>
              <CardDescription>Subject to final documentation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {[
                  { label: 'Target Amount', value: formatCurrency(deal.target_amount), icon: DollarSign },
                  { label: 'Minimum Investment', value: formatCurrency(deal.minimum_investment), icon: DollarSign },
                  { label: 'Interest Rate', value: `${deal.interest_rate_min}% - ${deal.interest_rate_max}% p.a.`, icon: Percent },
                  { label: 'Term', value: `${deal.term_months} months`, icon: Timer },
                  { label: 'Payment Frequency', value: deal.payment_frequency?.charAt(0).toUpperCase() + deal.payment_frequency?.slice(1), icon: Calendar },
                  { label: 'Interest Type', value: deal.compounding === 'simple' ? 'Simple Interest' : 'Compound Interest', icon: TrendingUp },
                  { label: 'Security/Collateral', value: deal.collateral_description, icon: Lock },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 py-4">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-[#6C757D]" />
                      </div>
                      <div>
                        <p className="text-sm text-[#6C757D]">{item.label}</p>
                        <p className="font-medium text-[#212529]">{item.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {deal.covenants && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-semibold text-[#212529] mb-2">Covenants</h4>
                  <p className="text-[#6C757D]">{deal.covenants}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Deal Documents</CardTitle>
              <CardDescription>Review all available documentation</CardDescription>
            </CardHeader>
            <CardContent>
              {!deal.documents || deal.documents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-[#6C757D]">No documents available yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {deal.documents.map((doc) => (
                    <div 
                      key={doc.id}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-[#1D2D2A]" />
                        <div>
                          <p className="font-medium text-[#212529]">{doc.document_name}</p>
                          <p className="text-xs text-[#6C757D]">
                            {doc.document_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadDocument(doc)}
                        className="border-gray-200"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Participate Tab */}
        <TabsContent value="participate">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Submit Expression of Interest</CardTitle>
              <CardDescription>
                {canInvest 
                  ? 'Express your interest in participating in this investment opportunity'
                  : deal.investor_eoi 
                    ? 'You have already submitted an expression of interest for this deal'
                    : 'This deal is no longer accepting new investments'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {canInvest ? (
                <div className="space-y-6 max-w-lg">
                  {/* Investment Amount */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#212529]">Investment Amount (ZAR)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]">R</span>
                      <Input
                        type="number"
                        value={eoiAmount}
                        onChange={(e) => setEoiAmount(e.target.value)}
                        className="pl-8 border-gray-200"
                        placeholder="Enter amount"
                        min={deal.minimum_investment}
                        step={1000}
                        data-testid="eoi-amount-input"
                      />
                    </div>
                    <p className="text-xs text-[#6C757D]">
                      Minimum investment: {formatCurrency(deal.minimum_investment)}
                    </p>
                  </div>
                  
                  {/* Confirmations */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="terms"
                        checked={termsAccepted}
                        onCheckedChange={setTermsAccepted}
                        data-testid="terms-checkbox"
                      />
                      <label htmlFor="terms" className="text-sm text-[#212529] cursor-pointer">
                        I have read and accept the indicative terms and conditions for this investment opportunity.
                      </label>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="risk"
                        checked={riskAcknowledged}
                        onCheckedChange={setRiskAcknowledged}
                        data-testid="risk-checkbox"
                      />
                      <label htmlFor="risk" className="text-sm text-[#212529] cursor-pointer">
                        I acknowledge the risks associated with this investment, including potential loss of capital.
                      </label>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="fica"
                        checked={ficaConfirmed}
                        onCheckedChange={setFicaConfirmed}
                        data-testid="fica-checkbox"
                      />
                      <label htmlFor="fica" className="text-sm text-[#212529] cursor-pointer">
                        I confirm that my FICA documentation is up to date and valid.
                      </label>
                    </div>
                  </div>
                  
                  {/* Notes */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-[#212529]">Notes (optional)</label>
                    <Textarea
                      value={eoiNotes}
                      onChange={(e) => setEoiNotes(e.target.value)}
                      placeholder="Any additional information..."
                      rows={3}
                      className="border-gray-200"
                    />
                  </div>
                  
                  {/* Warning */}
                  <div className="p-4 bg-amber-50 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Important Notice</p>
                      <p>Submitting an EOI is not a commitment. Final investment is subject to documentation and acceptance by Plane Tree Capital.</p>
                    </div>
                  </div>
                  
                  {/* Submit Button */}
                  <Button
                    className="w-full bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                    onClick={handleSubmitEOI}
                    disabled={submitting || !termsAccepted || !riskAcknowledged || !ficaConfirmed}
                    data-testid="submit-eoi-btn"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {submitting ? 'Submitting...' : 'Submit Expression of Interest'}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  {deal.investor_eoi ? (
                    <>
                      <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                      <p className="text-[#212529] font-medium">EOI Already Submitted</p>
                      <p className="text-[#6C757D] text-sm mt-2">
                        Your expression of interest for {formatCurrency(deal.investor_eoi.amount)} is {deal.investor_eoi.status}.
                      </p>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-[#212529] font-medium">Not Available</p>
                      <p className="text-[#6C757D] text-sm mt-2">
                        This deal is no longer accepting new investments.
                      </p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Success Dialog */}
      <Dialog open={successDialog} onOpenChange={setSuccessDialog}>
        <DialogContent>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <DialogTitle className="text-center">Expression of Interest Submitted!</DialogTitle>
            <DialogDescription className="text-center">
              Your EOI for {formatCurrency(parseFloat(eoiAmount))} has been submitted successfully. 
              Our team will review your submission and get back to you shortly.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-center">
            <Button
              className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
              onClick={() => setSuccessDialog(false)}
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DealDetailPage;
